import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getErrorMessage } from '../lib/errors'
import { toast } from 'sonner'
import { supabase } from '../lib/supabase'
import { useAppStore } from '../stores/app'

export interface Persona {
  id: string
  organization_id: string
  name: string
  role: string
  description: string | null
  voice_config: Record<string, unknown> | null
  expertise_areas: string[] | null
  platforms: string[] | null
  signature_phrases: string[] | null
  avatar_url: string | null
  created_at: string
  updated_at: string
}

interface PersonaCreate {
  name: string
  role: string
  description?: string
  voice_config?: Record<string, unknown>
  expertise_areas?: string[]
  platforms?: string[]
  signature_phrases?: string[]
  avatar_url?: string
}

export function usePersonas() {
  const organizationId = useAppStore((state) => state.organizationId)
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['personas', organizationId],
    queryFn: async () => {
      if (!organizationId) return []
      
      const { data, error } = await supabase
        .from('personas')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Persona[]
    },
    enabled: !!organizationId,
  })

  const createMutation = useMutation({
    mutationFn: async (persona: PersonaCreate) => {
      if (!organizationId) throw new Error('No organization ID')
      
      const { data, error } = await supabase
        .from('personas')
        .insert({ ...persona, organization_id: organizationId })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas', organizationId] })
      toast.success('Persona creada correctamente')
    },
    onError: (err) => {
      toast.error('Error al crear la persona', {
        description: getErrorMessage(err),
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Persona> & { id: string }) => {
      const { data, error } = await supabase
        .from('personas')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas', organizationId] })
      toast.success('Persona actualizada correctamente')
    },
    onError: (err) => {
      toast.error('Error al actualizar la persona', {
        description: getErrorMessage(err),
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const [tasksResult, piecesResult] = await Promise.all([
        supabase.from('content_tasks').select('id').eq('persona_id', id).limit(1),
        supabase.from('content_pieces').select('id').eq('persona_id', id).limit(1),
      ])
      if (tasksResult.error) throw tasksResult.error
      if (piecesResult.error) throw piecesResult.error
      const hasTasks = (tasksResult.data?.length ?? 0) > 0
      const hasPieces = (piecesResult.data?.length ?? 0) > 0
      if (hasTasks || hasPieces) {
        throw new Error('No se puede eliminar: esta persona tiene tareas o piezas de contenido asociadas.')
      }

      const { error } = await supabase
        .from('personas')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personas', organizationId] })
      toast.success('Persona eliminada correctamente')
    },
    onError: (err) => {
      toast.error('No se pudo eliminar la persona', {
        description: getErrorMessage(err),
      })
    },
  })

  return {
    ...query,
    createPersona: createMutation.mutateAsync,
    updatePersona: updateMutation.mutateAsync,
    deletePersona: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
